/**
 * referenceCategoryLabels — human-readable display labels for the
 * raw `DataSource.field` strings that data providers stamp onto
 * `report.references[]` entries. AP1.
 *
 * The provider layer uses field-name strings (`climate`, `waterStress`,
 * `siteResearch`, …) as the field key so downstream consumers can
 * route a row to the section it backs. Those raw strings used to leak
 * straight into the appendix UI ("climate — Open-Meteo …"), which read
 * as a debug stamp rather than a category label.
 *
 * AP1 maps each known raw key to a display string. Unknown keys fall
 * back to a title-cased version of the key so we never render the raw
 * camelCase token, but a regression is detectable (we add a label to
 * the table the moment a new provider field appears in a review).
 *
 * Co-located with `formatReferenceCategory` which also strips the
 * trailing ` (cached)` suffix providers append when the row was served
 * from the doc cache — internal cache state isn't reviewer-relevant.
 */
export const REFERENCE_CATEGORY_LABEL: Record<string, string> = {
  // Provider-class — DAE upstream API rows.
  climate: 'Climate',
  waterStress: 'Water Stress',
  seismic: 'Seismic Hazard',
  grid: 'Grid Infrastructure',
  flood: 'Flood Risk',
  environmental: 'Environmental Constraints',
  nuclearInfra: 'Nuclear Infrastructure',
  nuclear_infrastructure: 'Nuclear Infrastructure',
  solar: 'Solar Resource',
  wind: 'Wind Resource',
  electricity: 'Electricity Pricing',
  airQuality: 'Air Quality',
  connectivity: 'Connectivity',
  parcel: 'Parcel & Land Use',
  coordinates: 'Coordinates',
  'land.floodRisk': 'Flood Risk',
  // AI-research rows.
  siteResearch: 'AI Research',
  siteProfile: 'Site Profile',
  // Knowledge base + industry benchmarks.
  kb: 'Knowledge Base',
  adams: 'NRC ADAMS',
  adamsDocuments: 'NRC ADAMS',
  nrc_documents: 'NRC ADAMS',
  regulatory: 'Regulatory',
  benchmark: 'Industry Benchmark',
  workloadPowerFit: 'Workload & Power Reference',
};

/**
 * Strip the ` (cached)` provider-status suffix from a source name.
 * The suffix is stamped by `services/dataProviders/cache.ts` and
 * `services/siteEvidence.ts` when a row was served from the doc cache;
 * useful for ops, noise to a reviewer.
 */
export function stripCachedSuffix(source: string): string {
  return source.replace(/\s*\(cached\)\s*$/i, '').trimEnd();
}

/**
 * Resolve a DataSource.field to a display label. Unknown keys fall
 * back to a title-cased projection of the camelCase token so the
 * appendix never renders raw provider keys.
 */
export function formatReferenceCategory(field: unknown): string {
  // Older saved and sanitized references may omit this display-only key.
  // Keep their source/lineage visible without guessing a category or crashing.
  if (typeof field !== 'string' || !field.trim()) return 'Unspecified category';
  const known = Object.hasOwn(REFERENCE_CATEGORY_LABEL, field) ? REFERENCE_CATEGORY_LABEL[field] : undefined;
  if (known) return known;
  // Title-case fallback for unknown keys: turn `someNewField` into
  // "Some New Field". Hyphens / underscores split into words too.
  return field
    .replace(/[_\-.]+/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim()
    .split(' ')
    .map(w => (w ? w[0]!.toUpperCase() + w.slice(1) : ''))
    .join(' ');
}
